import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import { SelectMultiple, ChangeContext } from 'tea-component';
import useSearch from '@gp/components/Select/_hooks/useSearch';
import useSearchSelect from './_hooks/useSearchSelect';
import { defaultSelectFilter } from '@gp/utils/common';
import { getPlaceholder, getSelectPanelHeight } from '@gp/utils/select';
import { formatOptions } from '@gp/components/Select/utils';
import { defaultOptionTextRenderer } from '@gp/components/Select/utils/renderers';
import { getOptionValues, getSafetyValues, getSortedValues, getDividedOptions } from './utils';
import { renderButton } from './utils/renderers';
import { ALL_OPTION, GROUPS } from '@gp/constants/select';

import './index.scss';

const GPSelectMultiple: React.FC<IGPSelectMultipleProps> = (props) => {
  const {
    className,
    overlayClassName,
    boxClassName,
    showLabel = true,
    label = 'Metrics : ',
    placeholder: placeholderProps,
    appearance = 'button',
    matchButtonWidth: matchButtonWidthProps = true,
    listWidth,
    listHeight: listHeightProps,
    virtual: virtualProps,
    required = true,
    sortable = true,
    staging = false,
    searchable = false,
    searchPlaceholder: searchPlaceholderProps = 'Search',
    filter = defaultSelectFilter,
    searchValue: searchValueProps = '',
    onSearchValueChange: onSearchValueChangeProps,
    onSearch: onSearchProps,
    allOption = ALL_OPTION,
    groups = GROUPS,
    button: buttonProps,
    optionTextRenderer = defaultOptionTextRenderer,
    options: optionsProps,
    value: valueProps = [],
    onChange,
    onOpen,
    onClose,
    ...restProps
  } = props;

  const placeholder = placeholderProps ?? getPlaceholder('selectMultiple', label);
  const listHeight = getSelectPanelHeight(searchable, listHeightProps);
  const matchButtonWidth = listWidth ? false : matchButtonWidthProps;

  const { searchMode, searchValue, onSearchValueChange, onSearch } = useSearch({
    searchable,
    searchValue: searchValueProps,
    onSearchValueChange: onSearchValueChangeProps,
    onSearch: onSearchProps,
  });

  const [dividedOptions, setDividedOptions] = useState<IGPSelectOptionWithGroup[]>([]);

  const [value, setValue] = useState<string[]>([]);

  const options = formatOptions(optionsProps, {
    searchable,
    searchValue,
    optionTextRenderer,
  });

  const { searchingGroups, searchingOptions, searchingValue, onChangeWhenSearching } = useSearchSelect({
    searchMode,
    searchValue,
    filter,
    options,
    value,
    setValue,
  });

  const button = renderButton(buttonProps, {
    allOption,
    options,
    value,
    showLabel,
    label,
    optionTextRenderer,
  });

  const emitValue: IGPSelectMultipleChangeHandler = (payload, context) => {
    if (isEqual(payload, valueProps)) return;
    onChange?.(payload, context);
  };

  const getEmitValue = (payload?: string[] | null): string[] => {
    let payloadTmp = getSafetyValues(required, payload, options);
    if (sortable) {
      payloadTmp = getSortedValues(required, payloadTmp, options);
    }
    return payloadTmp;
  };

  const handleChange = (payload: string[], context: ChangeContext<React.SyntheticEvent<Element, Event>>) => {
    if (searchMode) {
      onChangeWhenSearching(payload, context);
      return;
    }
    let payloadTmp = !payload ? [] : payload;
    if (staging) {
      payloadTmp = getEmitValue(payloadTmp);
      emitValue(payloadTmp, context);
    }
    setValue(payloadTmp);
  };

  const handleSearch = (payload?: string, context?: ChangeContext<React.SyntheticEvent<Element, Event>>) => {
    if (!payload) {
      const options = formatOptions(optionsProps, {
        searchable,
        searchValue: payload,
        optionTextRenderer,
      });
      const dividedOptionsTmp = getDividedOptions(options, value);
      setDividedOptions(dividedOptionsTmp);
    }
    onSearch(payload, context);
  };

  const handleOpen = () => {
    const dividedOptionsTmp = getDividedOptions(options, value);
    setDividedOptions(dividedOptionsTmp);
    onOpen?.();
  };

  const handleClose = () => {
    if (!staging) {
      const valueTmp = getEmitValue(value);
      emitValue(valueTmp, null);
      setValue(valueTmp);
    }
    onClose?.();
  };

  useEffect(() => {
    const allValues = getOptionValues(optionsProps);
    let validValue = valueProps?.filter((item) => allValues?.includes(item)) || [];
    if (required && !validValue?.length) {
      validValue = [...allValues];
    }
    if (!isEqual(value, validValue)) {
      setValue(validValue);
    }
  }, [optionsProps, valueProps]);

  return (
    <SelectMultiple
      {...restProps}
      className={classnames('dbg-common-select-multiple', className)}
      overlayClassName={classnames(
        'dbg-common-select-multiple-overlay globalpedia-custom-scrollbar',
        overlayClassName,
        {
          'dbg-common-select-multiple-overlay--empty': !options?.length,
          'dbg-common-select-multiple-overlay--all-option': !searchMode && !!allOption,
          'dbg-common-select-multiple-overlay--searching': searchMode,
          'dbg-common-select-multiple-overlay--searching-empty': searchMode && !searchingOptions?.length,
        },
      )}
      boxClassName={classnames('dbg-common-select-multiple-box', boxClassName)}
      placeholder={placeholder}
      appearance={appearance}
      matchButtonWidth={!options?.length ? true : matchButtonWidth}
      listWidth={listWidth}
      listHeight={listHeight}
      // 无数据时启用虚拟滚动，确保上一次下拉项太多导致刷新下拉项异常
      virtual={virtualProps ?? !options?.length}
      staging={staging}
      searchable={searchable}
      searchPlaceholder={searchPlaceholderProps}
      filter={filter}
      searchValue={searchValue}
      onSearchValueChange={onSearchValueChange}
      onSearch={handleSearch}
      allOption={searchMode ? false : allOption}
      groups={searchMode ? searchingGroups : groups}
      button={button}
      options={searchMode ? searchingOptions : dividedOptions}
      value={searchMode ? searchingValue : value}
      onChange={handleChange}
      onOpen={handleOpen}
      onClose={handleClose}
    />
  );
};

export default GPSelectMultiple;
